import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import type { ThemeMode } from '../styles/Themes';

const STORAGE_KEY = '@Jj:theme';

interface ThemeModeContextData {
    mode: ThemeMode;
    isDark: boolean;
    toggle: () => void;
    setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextData>(
    {} as ThemeModeContextData
);

function getInitialMode(): ThemeMode {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === 'light' || saved === 'dark') return saved;
    } catch (_) {
        /* localStorage indisponível — segue com default */
    }
    // Default: dark. O Legal Ops adota o tema escuro como padrão (EASYJUR-5735 #342);
    // o usuário pode alternar para light a qualquer momento (persistido no localStorage).
    return 'dark';
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, mode);
        } catch (_) {
            /* ignore */
        }
        // OBS: o atributo data-theme no <html> é setado no App.tsx (AppContent),
        // que considera o "modo efetivo" (telas externas são dark-only).
    }, [mode]);

    const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
    const toggle = useCallback(
        () => setModeState((prev) => (prev === 'dark' ? 'light' : 'dark')),
        []
    );

    return (
        <ThemeModeContext.Provider
            value={{ mode, isDark: mode === 'dark', toggle, setMode }}
        >
            {children}
        </ThemeModeContext.Provider>
    );
}

export function useThemeMode() {
    return useContext(ThemeModeContext);
}
